(() => {
  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const initializeDrawers = (context = document) => {
    qa('[data-drawer-open]:not([data-ready])', context).forEach((button) => {
      const drawer = document.getElementById(button.getAttribute('aria-controls'));
      if (!drawer) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => {
        drawer.showModal();
        button.setAttribute('aria-expanded', 'true');
      });
      qa('[data-drawer-close]', drawer).forEach((close) => close.addEventListener('click', () => drawer.close()));
      drawer.addEventListener('close', () => button.setAttribute('aria-expanded', 'false'));
    });
  };
  class Revolver {
    constructor(root) {
      this.root = root;
      this.cards = qa('[data-revolver-card]', root);
      this.panels = qa('[data-revolver-panel]', root);
      this.dots = qa('[data-revolver-dot]', root);
      this.counter = q('[data-revolver-counter]', root);
      this.active = Math.max(0, Number(root.dataset.initial) || 0);
      this.pointerStart = null;
      this.dragDelta = 0;
      this.timer = null;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      if (!this.cards.length) return;
      root.dataset.ready = 'true';
      this.bind();
      this.go(this.active, false);
      this.start();
    }
    bind() {
      q('[data-revolver-prev]', this.root)?.addEventListener('click', () => this.go(this.active - 1), { signal: this.signal });
      q('[data-revolver-next]', this.root)?.addEventListener('click', () => this.go(this.active + 1), { signal: this.signal });
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.index)), { signal: this.signal }));
      this.cards.forEach((card) => card.addEventListener('click', (event) => {
        const index = Number(card.dataset.index);
        if (index !== this.active) { event.preventDefault(); this.go(index); }
      }, { signal: this.signal }));
      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.active - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.active + 1); }
      }, { signal: this.signal });
      const stage = q('[data-revolver-stage]', this.root);
      stage?.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        this.pointerStart = event.clientX; this.dragDelta = 0; this.root.classList.add('is-dragging');
        stage.setPointerCapture?.(event.pointerId); this.stop();
      }, { signal: this.signal });
      stage?.addEventListener('pointermove', (event) => { if (this.pointerStart !== null) this.dragDelta = event.clientX - this.pointerStart; }, { signal: this.signal });
      stage?.addEventListener('pointerup', () => this.finishDrag(), { signal: this.signal });
      stage?.addEventListener('pointercancel', () => this.finishDrag(), { signal: this.signal });
      this.root.addEventListener('mouseenter', () => this.stop(), { signal: this.signal });
      this.root.addEventListener('mouseleave', () => this.start(), { signal: this.signal });
      window.addEventListener('resize', () => this.position(), { signal: this.signal });
    }
    finishDrag() {
      if (this.pointerStart === null) return;
      if (Math.abs(this.dragDelta) > 45) this.go(this.active + (this.dragDelta < 0 ? 1 : -1));
      this.pointerStart = null; this.dragDelta = 0; this.root.classList.remove('is-dragging'); this.start();
    }
    normalize(index) { const length = this.cards.length; return ((index % length) + length) % length; }
    distance(index) {
      const length = this.cards.length; let difference = index - this.active;
      if (difference > length / 2) difference -= length;
      if (difference < -length / 2) difference += length;
      return difference;
    }
    position() {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const cardWidth = this.cards[0]?.getBoundingClientRect().width || 320;
      const step = cardWidth * (mobile ? .70 : Number(this.root.dataset.spacing) / 100);
      const depth = Number(this.root.dataset.depth) || 115;
      const curve = Number(this.root.dataset.curve) || 24;
      this.cards.forEach((card, index) => {
        const difference = this.distance(index); const absolute = Math.abs(difference); const visible = Math.max(-4, Math.min(4, difference));
        const x = visible * step; const y = Math.min(absolute, 4) * curve + Math.pow(Math.min(absolute, 4), 2) * 3;
        const z = -Math.min(absolute, 4) * depth; const rotation = visible * (mobile ? -5 : -8); const scale = Math.max(.68, 1 - absolute * (mobile ? .08 : .065));
        card.style.transform = `translateX(calc(-50% + ${x}px)) translateY(${y}px) translateZ(${z}px) rotateY(${rotation}deg) scale(${scale})`;
        card.style.opacity = absolute > 3 ? '0' : String(Math.max(.25, 1 - absolute * .12)); card.style.zIndex = String(20 - absolute);
        card.classList.toggle('is-active', difference === 0); card.setAttribute('aria-current', difference === 0 ? 'true' : 'false'); card.tabIndex = difference === 0 ? 0 : -1;
      });
    }
    go(index, animate = true) {
      this.active = this.normalize(index); if (!animate) this.root.classList.add('is-initializing'); this.position();
      this.panels.forEach((panel, i) => panel.classList.toggle('is-active', i === this.active));
      this.dots.forEach((dot, i) => { const selected = i === this.active; dot.classList.toggle('is-active', selected); dot.setAttribute('aria-pressed', selected ? 'true' : 'false'); });
      if (this.counter) this.counter.textContent = `${String(this.active + 1).padStart(2, '0')} / ${String(this.cards.length).padStart(2, '0')}`;
      if (!animate) requestAnimationFrame(() => this.root.classList.remove('is-initializing'));
    }
    start() {
      this.stop();
      if (this.root.dataset.autoplay !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.Shopify?.designMode) return;
      this.timer = window.setInterval(() => this.go(this.active + 1), Math.max(3000, Number(this.root.dataset.interval) || 6000));
    }
    stop() { if (this.timer) window.clearInterval(this.timer); this.timer = null; }
    destroy() { this.stop(); this.abort.abort(); delete this.root.dataset.ready; }
  }
  const initialize = (context = document) => {
    const roots = []; if (context.matches?.('[data-revolver]')) roots.push(context); roots.push(...qa('[data-revolver]:not([data-ready])', context));
    roots.forEach((root) => { root.revolver = new Revolver(root); });
  };
  document.addEventListener('DOMContentLoaded', () => { initialize(); initializeDrawers(); });
  document.addEventListener('shopify:section:load', (event) => { initialize(event.target); initializeDrawers(event.target); });
  document.addEventListener('shopify:section:unload', (event) => q('[data-revolver]', event.target)?.revolver?.destroy());
  document.addEventListener('shopify:block:select', (event) => {
    const card = event.target.matches?.('[data-revolver-card]') ? event.target : q('[data-revolver-card]', event.target);
    const revolver = card?.closest('[data-revolver]')?.revolver;
    if (card && revolver) { revolver.go(Number(card.dataset.index)); revolver.stop(); }
  });
})();
